import { useState } from "react";
import { Link } from "@inertiajs/react";
import { Heart, MessageCircle, Share2, Sprout, ChevronRight, CircleCheck, Eye, FileText, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Menu } from "@/components/Menu";
import { IconButton } from "@/components/IconButton";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { cn } from "@/lib/cn";
import { type StoryPost, type Comment, likePost, postLikers, deletePost, timeAgo } from "@/lib/feedApi";
import { Lightbox } from "./Lightbox";
import { LikersDialog } from "./LikersDialog";
import { ShareDialog } from "./ShareDialog";
import { EditDialog } from "./EditDialog";
import { StoryComments } from "./StoryComments";

interface Props {
  post: StoryPost;
  /** Detail mode: show full body and open comments by default. */
  full?: boolean;
  /** Server-provided comments (detail page) to avoid a refetch. */
  initialComments?: Comment[];
  /** Called after the post is successfully deleted. */
  onDeleted?: () => void;
}

export function StoryCard({ post, full = false, initialComments, onDeleted }: Props) {
  const [liked, setLiked] = useState(post.likedByMe);
  const [likeCount, setLikeCount] = useState(post.likeCount);
  const [commentCount, setCommentCount] = useState(post.commentCount);
  const [commentsOpen, setCommentsOpen] = useState(full);
  const [likersOpen, setLikersOpen] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [shareEnabled, setShareEnabled] = useState(post.shareEnabled);
  const [content, setContent] = useState({ title: post.title, excerpt: post.excerpt, body: post.body });

  async function confirmDelete() {
    await deletePost(post.id);
    setConfirmOpen(false);
    onDeleted?.();
  }

  async function toggleLike() {
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    try {
      const r = await likePost(post.id);
      setLiked(r.liked);
      setLikeCount(r.count);
    } catch {
      setLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
    }
  }

  return (
    <Card>
      <div className="px-4 pt-3.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2 text-sm">
            {post.projectName ? (
              <Link
                href={post.projectId ? `/projects/${post.projectId}` : "#"}
                className="inline-flex min-w-0 items-center gap-1.5 font-medium text-fg hover:text-accent-text transition-colors [&_svg]:size-4 [&_svg]:text-accent-text"
              >
                <Sprout /> <span className="truncate">{post.projectName}</span>
              </Link>
            ) : (
              <span className="text-fg-muted">Update</span>
            )}
            <span className="text-fg-subtle">·</span>
            <span className="shrink-0 text-xs text-fg-subtle tabular">{timeAgo(post.createdAt)}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {post.verified ? (
              <Badge tone="success">
                <CircleCheck /> Verified
              </Badge>
            ) : post.reviewed ? (
              <Badge tone="neutral">
                <Eye /> Reviewed
              </Badge>
            ) : post.draft ? (
              <Badge tone="warning">Draft</Badge>
            ) : null}

            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton size="sm" aria-label="Post actions" className="-mr-1">
                  <MoreHorizontal />
                </IconButton>
              </Menu.Trigger>
              <Menu.Content align="end">
                <Menu.Item onSelect={() => setEditOpen(true)}>
                  <Pencil /> Edit post
                </Menu.Item>
                <Menu.Item destructive onSelect={() => setConfirmOpen(true)}>
                  <Trash2 /> Delete post
                </Menu.Item>
              </Menu.Content>
            </Menu.Root>
          </div>
        </div>
      </div>

      <Card.Body className="flex flex-col gap-3 pt-3">
        {content.title && (
          <h3 className="text-base font-semibold tracking-tight text-fg">{content.title}</h3>
        )}

        <StoryMedia
          images={post.images}
          video={post.video}
          onOpen={(i) => setLightbox(i)}
        />

        {(full ? content.body : content.excerpt) && (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-fg-muted">
            {full ? content.body : content.excerpt}
          </p>
        )}

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 text-sm text-fg-muted">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleLike}
              className={cn(
                "inline-flex items-center gap-1.5 hover:text-fg [&_svg]:size-4",
                liked && "text-accent-text",
              )}
            >
              <Heart className={cn(liked && "fill-current")} />
              <span className="tabular">{likeCount}</span>
            </button>
            {likeCount > 0 && (
              <button type="button" onClick={() => setLikersOpen(true)} className="text-xs hover:text-fg">
                Who
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setCommentsOpen((o) => !o)}
            className={cn(
              "inline-flex items-center gap-1.5 hover:text-fg [&_svg]:size-4",
              commentsOpen && "text-fg",
            )}
          >
            <MessageCircle />
            <span className="tabular">{commentCount}</span>
          </button>

          {!post.draft && (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              aria-label="Share"
              className={cn(
                "inline-flex items-center gap-1.5 hover:text-fg [&_svg]:size-4",
                shareEnabled && "text-accent-text",
              )}
            >
              <Share2 />
              <span className="hidden text-xs sm:inline">Share</span>
            </button>
          )}

          {post.topics.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {post.topics.map((t) => (
                <Link
                  key={t.slug}
                  href={`/feed?topics[]=${encodeURIComponent(t.slug)}`}
                  className="rounded-full bg-subtle px-2 py-0.5 text-xs text-fg-muted hover:bg-surface-hover hover:text-fg transition-colors"
                >
                  {t.name}
                </Link>
              ))}
            </div>
          )}

          {!full && (
            <Link
              href={post.detailUrl}
              className="ml-auto inline-flex items-center gap-0.5 text-xs font-medium text-accent-text hover:underline [&_svg]:size-3.5"
            >
              Read more <ChevronRight />
            </Link>
          )}
        </div>

        {commentsOpen && (
          <div className="border-t border-border-subtle pt-3">
            <StoryComments postId={post.id} initialComments={initialComments} onCountChange={setCommentCount} />
          </div>
        )}
      </Card.Body>

      {lightbox !== null && (
        <Lightbox images={post.images} startIndex={lightbox} onClose={() => setLightbox(null)} />
      )}
      {likersOpen && (
        <LikersDialog load={() => postLikers(post.id)} onClose={() => setLikersOpen(false)} />
      )}
      {shareOpen && (
        <ShareDialog
          postId={post.id}
          title={post.displayTitle}
          publicUrl={post.publicUrl}
          initialPublic={shareEnabled}
          onChange={setShareEnabled}
          onClose={() => setShareOpen(false)}
        />
      )}
      {editOpen && (
        <EditDialog
          post={{ ...post, title: content.title, body: content.body }}
          onClose={() => setEditOpen(false)}
          onUpdated={(u) => setContent({ title: u.title, excerpt: u.excerpt, body: u.body })}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete post?"
        message="This permanently removes the post and its comments. This can't be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={confirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </Card>
  );
}

function StoryMedia({
  images,
  video,
  onOpen,
}: {
  images: string[];
  video: string | null;
  onOpen: (index: number) => void;
}) {
  if (video) {
    return (
      <video
        src={video}
        controls
        preload="metadata"
        className="max-h-[28rem] w-full rounded-[var(--radius-md)] bg-black"
      />
    );
  }
  if (images.length === 0) return null;

  if (images.length === 1) {
    return (
      <button type="button" onClick={() => onOpen(0)} className="block overflow-hidden rounded-[var(--radius-md)]">
        <img
          src={images[0]}
          alt=""
          className="max-h-[28rem] w-full object-cover transition-transform hover:scale-[1.01]"
        />
      </button>
    );
  }

  const [first, ...rest] = images;
  const extra = rest.slice(2).length;
  return (
    <div className="grid grid-cols-2 gap-1.5 overflow-hidden rounded-[var(--radius-md)]">
      <button type="button" onClick={() => onOpen(0)} className="row-span-2 overflow-hidden">
        <img src={first} alt="" className="h-full max-h-[28rem] w-full object-cover transition-transform hover:scale-[1.01]" />
      </button>
      {rest.slice(0, 2).map((src, i) => (
        <button
          type="button"
          key={i}
          onClick={() => onOpen(i + 1)}
          className="relative overflow-hidden"
        >
          <img src={src} alt="" className="h-full max-h-[13.5rem] w-full object-cover transition-transform hover:scale-[1.01]" />
          {i === 1 && extra > 0 && (
            <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-semibold text-white">
              +{extra}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/* Re-exported icon kept for the empty-feed state in the page. */
export { FileText as FeedIcon };
